import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SelectObject } from '../../types/certificates';
import cn from '../cn';
import { getPopoverPositionStyle } from '../popoverPosition';
import { useAnchorRect } from '../useAnchorRect';

type AutocompleteSelectProps = {
  selectedItem?: string | number | null,
  width: number,
  widthPercentage?: boolean,
  filter_by_text: string,
  options?: string[],
  optionsObject?: SelectObject[],
  onSelectedItem: Function,
  required?: boolean,
  disabled?: boolean,
  editable?: boolean,
};

// MUI Autocomplete port: text-filterable combobox with optional freeSolo (editable).
// Case-insensitive substring filter; string-mode and object-mode; emits
// onSelectedItem({ target: { value } }) — value is the option string, object id,
// or (freeSolo) the raw typed string, matching the old wrapper.
const AutocompleteSelect: FunctionComponent<AutocompleteSelectProps> = React.memo(({
  selectedItem,
  required = false,
  width,
  filter_by_text,
  options,
  optionsObject,
  onSelectedItem,
  widthPercentage = false,
  disabled = false,
  editable = false,
}) => {
  const containerStyle = widthPercentage === false
    ? { minWidth: width + 'px', maxWidth: width + 'px' }
    : { width: width + '%' };

  const selectedLabel = options !== undefined
    ? ((selectedItem as string) ?? '')
    : (optionsObject?.find((o) => o.id === selectedItem)?.name ?? '');

  const [inputValue, setInputValue] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // keep the field in sync with the selected value when not actively editing
  useEffect(() => { if (!open) setInputValue(selectedLabel); }, [selectedLabel, open]);

  // Live rect: follows the anchor when a dialog body or the page scrolls.
  const rect = useAnchorRect(open, rootRef, [inputValue]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      if (listRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const all: { value: string | number; label: string }[] = options !== undefined
    ? options.map((o) => ({ value: o, label: o }))
    : (optionsObject ?? []).map((o) => ({ value: o.id, label: o.name }));

  const filtered = all.filter((o) => o.label.toLowerCase().includes(inputValue.toLowerCase()));

  const commit = (value: string | number | null) => {
    onSelectedItem({ target: { value } });
    setOpen(false);
  };

  const onInput = (v: string) => {
    setInputValue(v);
    setOpen(true);
    setActive(-1);
    // freeSolo: typing itself updates the value (string/object modes both allow null)
    if (editable) onSelectedItem({ target: { value: v ?? (options !== undefined ? '' : null) } });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && active >= 0 && filtered[active]) { e.preventDefault(); pick(filtered[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const pick = (o: { value: string | number; label: string }) => {
    setInputValue(o.label);
    commit(o.value);
  };


  const popoverPosition = rect ? getPopoverPositionStyle(rect) : null;

  return (
    <div ref={rootRef} style={containerStyle} className="ds-control-root relative inline-flex flex-col align-top">
      <label className="ds-label">
        {filter_by_text}{required ? ' *' : ''}
      </label>
      <input
        value={inputValue}
        disabled={disabled}
        onChange={(e) => onInput(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="ds-field w-full"
      />

      {open && rect && filtered.length > 0 && createPortal(
        <ul
          ref={listRef}
          role="listbox"
          style={popoverPosition!.style}
          className={cn('ds-menu scrollbar-thin', popoverPosition!.className)}
        >
          {filtered.map((o, i) => (
            <li
              key={i}
              role="option"
              aria-selected={active === i}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(o)}
              className="ds-menu-item"
            >
              {o.label}
            </li>
          ))}
        </ul>,
        document.getElementById('root') ?? document.body,
      )}
    </div>
  );
});

export default AutocompleteSelect;
